import type { IconName } from "@/components/icons/Icon";
import type { ProjectNavbarLink } from "@/components/navigation/ProjectNavbarCommon.constants";
import { FeatureFlagLiteral } from "@/utils/hooks/use-flags-grpc";

export interface SettingSidebarItem {
  id: string;
  name: string;
  /** Extra command-palette query terms for concepts this pane owns but does not name. */
  searchTerms?: readonly string[];
  flag?: FeatureFlagLiteral | null | undefined;
  shouldShowInternal?: boolean;
  chalkEmployeeOnly?: boolean;
  deprecationNotice?: string;
  hideFromSettingsMenu?: boolean;
  hideInServerless?: boolean;
  /** Destination for panes that live outside `/settings/<id>`. */
  href?: (projectId: string, environmentId: string) => string;
  /** Lists the pane only when the environment has this resource; see `ProjectNavbarLink`. */
  environmentResourceIdField?: ProjectNavbarLink<string>["environmentResourceIdField"];
}

export interface SettingsSidebarItemGroup {
  title: string;
  panes: SettingSidebarItem[];
}

export const SETTINGS_SIDEBAR_ITEMS = [
  {
    title: "Team",
    panes: [
      {
        id: "billing",
        name: "Billing",
        flag: "stripe_billing_enabled",
      },
      {
        // note: custom display behavior determined by flag `resource_usage_disabled`
        id: "usage-dashboard",
        name: "Usage",
        shouldShowInternal: true,
      },
      {
        id: "shared-resources",
        name: "Shared resources",
        deprecationNotice: "Shared resources have moved to Infrastructure/cluster-level settings.",
        hideInServerless: true,
      },
      {
        id: "infra-pipelines",
        name: "Infra pipelines",
        shouldShowInternal: true,
        flag: "metadata_argo_workflows_enabled",
      },
      {
        id: "github",
        name: "GitHub",
      },
    ],
  },
  {
    title: "Environment",
    panes: [
      {
        id: "general",
        name: "General",
      },
      {
        id: "permissions",
        name: "Users and roles",
      },
      {
        id: "tokens",
        name: "Access tokens",
      },
      {
        id: "cli-login",
        name: "CLI login",
        chalkEmployeeOnly: true,
      },
      {
        id: "connections",
        name: "Connections",
      },
      {
        id: "assistant",
        name: "Assistant",
        searchTerms: [
          "conversations",
          "chat history",
          "model providers",
          "ai providers",
          "ai connections",
        ],
      },
      {
        id: "compute",
        name: "Compute",
        searchTerms: ["host pools"],
        hideInServerless: true,
      },
      {
        id: "quotas",
        name: "Quotas",
        flag: "quotas_enabled",
        searchTerms: ["limits", "gpu quota", "quota increase", "sandbox limit"],
      },
      {
        id: "host-capacity",
        name: "Host capacity",
        searchTerms: ["host pools", "sandbox capacity", "unschedulable", "hosts", "external hosts"],
        chalkEmployeeOnly: true,
      },
      {
        id: "buckets",
        name: "Buckets",
      },
      {
        id: "feature-flags",
        name: "Feature flags",
        chalkEmployeeOnly: true,
      },
      {
        id: "notebooks",
        name: "Notebooks",
        flag: "notebooks_enabled",
      },
      {
        id: "suspend",
        name: "Suspend",
      },
      {
        id: "archive",
        name: "Archive",
      },
      {
        // Clears out a self-signup team so the tester's email is free again.
        // Employee-only in the menu; the endpoint enforces chalk.admin itself.
        id: "delete-team",
        name: "Delete team",
        searchTerms: ["self-signup", "signup"],
        chalkEmployeeOnly: true,
      },
    ],
  },
] as const;

export const SETTINGS_SIDEBAR_ITEMS_FLAT: SettingSidebarItem[] = SETTINGS_SIDEBAR_ITEMS.flatMap(
  (pane): readonly SettingSidebarItem[] => pane.panes,
);

export type SettingsNavbarId =
  | "billing"
  | "usage-dashboard"
  | "shared-resources"
  | "infra-pipelines"
  | "sso"
  | "github"
  | "general"
  | "permissions"
  | "tokens"
  | "cli-login"
  | "snowflake"
  | "connections"
  | "compute"
  | "host-capacity"
  | "quotas"
  | "buckets"
  | "feature-flags"
  | "notebooks"
  | "ai-connections"
  | "assistant"
  | "debug-chart"
  | "suspend"
  | "archive"
  | "delete-team"
  | "kubernetes"
  | "alert-config"
  | "artifact-repositories"
  | "build-profile"
  | "environment-variables"
  | "global-metrics"
  | "incident-io"
  | "logstash"
  | "nodepools"
  | "pager-duty"
  | "resources"
  | "slack"
  | "trusted-ips"
  | "data_sources"
  | "online-store"
  | "offline-store"
  | "vector-db"
  | "secrets"
  | "resource-group-jobs"
  | "cloud-overview"
  | "metrics-database"
  | "background-persistence"
  | "workflow-orchestrator-engine"
  | "clickhouse"
  | "audit-logs";

export const SETTINGS_NAV_ICONS: Partial<Record<SettingsNavbarId, IconName>> = {
  billing: "building",
  "usage-dashboard": "chartColumnBig",
  "shared-resources": "blend",
  "infra-pipelines": "route",
  sso: "scanFace",
  github: "gitHub",
  general: "settings",
  permissions: "userRound",
  tokens: "key",
  "cli-login": "terminal",
  snowflake: "snowflake",
  connections: "link",
  compute: "cpu",
  "host-capacity": "gauge",
  quotas: "scale",
  buckets: "boxes",
  "feature-flags": "flag",
  notebooks: "notebook",
  "ai-connections": "brain",
  assistant: "messageCircle",
  "debug-chart": "bug",
  suspend: "octagonPause",
  archive: "archive",
  "delete-team": "delete",
};
